import React from 'react';
import { Loader2, Plus, RefreshCw, UserRound } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import SearchableSelect from '@/components/shared/SearchableSelect';
import BankAccountModal from '@/components/employees/BankAccountModal';
import {
  accountingApi,
  type BankOption,
  type BeneficiaryBankAccount,
  type LookupBeneficiary,
} from '@/lib/api/accounting';
import { formatIranIbanDashed, isCompleteIranIban, accountNumberFromSaderatIban } from '@/lib/bsiTransferMode';

export type SystemShebaSelection = {
  iban: string;
  accountNumber: string;
  firstName: string;
  lastName: string;
  accountHolderName: string;
  employeeLabel: string;
};

type DestinationMode = 'iban' | 'account';

type Props = {
  onSelect: (selection: SystemShebaSelection) => void;
  disabled?: boolean;
  /** iban: ساتنا/پایا — account: انتقال صادراتی با شماره حساب */
  mode?: DestinationMode;
};

function splitPersonName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function parseEmployeeNumericId(employeeKey: string): number | null {
  const m = /^employee-(\d+)$/.exec(employeeKey);
  return m ? Number(m[1]) : null;
}

function isEmployeeSystemAccount(account: BeneficiaryBankAccount): boolean {
  return account.id.startsWith('employee-account-');
}

function accountSheba(account: BeneficiaryBankAccount): string {
  return formatIranIbanDashed(account.sheba_number || '');
}

function accountNumberDigits(account: BeneficiaryBankAccount): string {
  const stored = (account.account_number || '').replace(/\D/g, '');
  const fromSheba = accountNumberFromSaderatIban(account.sheba_number) || '';
  if (fromSheba) {
    if (!stored) return fromSheba;
    if (stored === fromSheba) return stored;
    // نسخهٔ ذخیره‌شده صفر اول را از دست داده (مثل 112437073005 به‌جای 0112437073005)
    if (fromSheba.endsWith(stored) && stored.length < fromSheba.length) {
      return fromSheba;
    }
  }
  return stored.length >= 8 ? stored : fromSheba;
}

function needsAccountNumberPersist(account: BeneficiaryBankAccount): boolean {
  if (!isEmployeeSystemAccount(account)) return false;
  const stored = (account.account_number || '').replace(/\D/g, '');
  const resolved = accountNumberDigits(account);
  return resolved.length >= 8 && stored !== resolved;
}

function hasUsableCard(account: BeneficiaryBankAccount): boolean {
  return (account.card_number || '').replace(/\D/g, '').length >= 16;
}

function hasUsableAccountNumber(account: BeneficiaryBankAccount): boolean {
  return accountNumberDigits(account).length >= 8;
}

function looksLikeSaderat(account: BeneficiaryBankAccount): boolean {
  const label = `${account.label || ''}`.toLowerCase();
  if (/صادرات|saderat|bsi/.test(label)) return true;
  return !!accountNumberFromSaderatIban(account.sheba_number);
}

function accountOptionLabel(account: BeneficiaryBankAccount, mode: DestinationMode): string {
  if (mode === 'account') {
    const acc = accountNumberDigits(account);
    const stored = (account.account_number || '').replace(/\D/g, '');
    const fromSheba = !!accountNumberFromSaderatIban(account.sheba_number) && stored !== acc;
    if (acc) {
      return `${account.label} · ${acc}${fromSheba ? ' · از شبا' : ''}${looksLikeSaderat(account) ? ' · صادرات' : ''}`;
    }
    if (isCompleteIranIban(account.sheba_number)) {
      return `${account.label} · شبا غیرصادراتی / غیرقابل استخراج`;
    }
    if (hasUsableCard(account)) {
      return `${account.label} · کارت دارد (بدون شماره حساب)`;
    }
    return `${account.label} · بدون شماره حساب`;
  }

  const sheba = account.sheba_number ? formatIranIbanDashed(account.sheba_number) : '';
  if (isCompleteIranIban(account.sheba_number)) {
    return `${account.label} · ${sheba}`;
  }
  if (hasUsableCard(account)) {
    return `${account.label} · کارت دارد (بدون شبا)`;
  }
  return `${account.label} · بدون شبا`;
}

function pickPreferredAccount(list: BeneficiaryBankAccount[], mode: DestinationMode, preferredId?: string) {
  if (preferredId) {
    const preferred = list.find((a) => a.id === preferredId);
    if (preferred) return preferred;
  }
  if (mode === 'account') {
    return (
      list.find((a) => a.is_default && hasUsableAccountNumber(a) && looksLikeSaderat(a)) ||
      list.find((a) => hasUsableAccountNumber(a) && looksLikeSaderat(a)) ||
      list.find((a) => a.is_default && hasUsableAccountNumber(a)) ||
      list.find((a) => hasUsableAccountNumber(a)) ||
      list.find((a) => a.is_default) ||
      list[0]
    );
  }
  return (
    list.find((a) => a.is_default && isCompleteIranIban(a.sheba_number)) ||
    list.find((a) => isCompleteIranIban(a.sheba_number)) ||
    list.find((a) => a.is_default) ||
    list[0]
  );
}

const BsiSystemShebaPicker: React.FC<Props> = ({ onSelect, disabled, mode = 'iban' }) => {
  const [employees, setEmployees] = React.useState<LookupBeneficiary[]>([]);
  const [employeeId, setEmployeeId] = React.useState('');
  const [accounts, setAccounts] = React.useState<BeneficiaryBankAccount[]>([]);
  const [accountId, setAccountId] = React.useState('');
  const [banks, setBanks] = React.useState<BankOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = React.useState(false);
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [showBankModal, setShowBankModal] = React.useState(false);
  const [converting, setConverting] = React.useState(false);
  const [statusMsg, setStatusMsg] = React.useState<{ type: 'ok' | 'err' | 'info'; text: string } | null>(
    null,
  );
  const onSelectRef = React.useRef(onSelect);
  onSelectRef.current = onSelect;

  const numericEmployeeId = parseEmployeeNumericId(employeeId);
  const selectedAccount = accounts.find((a) => a.id === accountId) || null;
  const selectedHasSheba = selectedAccount ? isCompleteIranIban(selectedAccount.sheba_number) : false;
  const selectedCanConvert =
    !!selectedAccount &&
    isEmployeeSystemAccount(selectedAccount) &&
    hasUsableCard(selectedAccount) &&
    (mode === 'iban'
      ? !selectedHasSheba
      : !hasUsableAccountNumber(selectedAccount) || !selectedHasSheba);

  const applyAccount = React.useCallback(
    (account: BeneficiaryBankAccount, employeeKey: string, employeeList: LookupBeneficiary[]) => {
      const employee = employeeList.find((e) => e.id === employeeKey);
      const fromHolder = splitPersonName(account.account_holder_name || employee?.name || '');
      const digits = accountNumberDigits(account);
      const shebaOk = isCompleteIranIban(account.sheba_number);

      if (mode === 'account') {
        if (!digits) return false;
        onSelectRef.current({
          iban: shebaOk ? accountSheba(account) : '',
          accountNumber: digits,
          firstName: fromHolder.firstName,
          lastName: fromHolder.lastName,
          accountHolderName: account.account_holder_name || employee?.name || '',
          employeeLabel: employee?.name || '',
        });
        return true;
      }

      if (!shebaOk) return false;
      onSelectRef.current({
        iban: accountSheba(account),
        accountNumber: digits,
        firstName: fromHolder.firstName,
        lastName: fromHolder.lastName,
        accountHolderName: account.account_holder_name || employee?.name || '',
        employeeLabel: employee?.name || '',
      });
      return true;
    },
    [mode],
  );

  const persistDerivedAccountNumber = React.useCallback(
    async (account: BeneficiaryBankAccount, employeeKey: string): Promise<boolean> => {
      const empNum = parseEmployeeNumericId(employeeKey);
      if (!empNum || !needsAccountNumberPersist(account)) return false;
      const resolved = accountNumberDigits(account);
      try {
        const meta = await accountingApi.employeeBankAccounts(empNum);
        const row = (meta.bank_accounts || []).find((a) => a.id === account.account_id);
        if (!row) return false;
        const stored = (row.account_number || '').replace(/\D/g, '');
        if (stored === resolved) return false;
        await accountingApi.updateEmployeeBankAccount(empNum, row.id, {
          bank_id: row.bank_id,
          account_holder_name: row.account_holder_name || account.account_holder_name || '',
          account_number: resolved,
          card_number: row.card_number || undefined,
          iban: row.iban || account.sheba_number || undefined,
          notes: row.notes || undefined,
          is_default: row.is_default,
          is_active: row.is_active,
        });
        return true;
      } catch {
        return false;
      }
    },
    [],
  );

  const loadAccounts = React.useCallback(
    async (employeeKey: string, preferredAccountId?: string) => {
      if (!employeeKey) {
        setAccounts([]);
        setAccountId('');
        setBanks([]);
        return;
      }
      setLoadingAccounts(true);
      setStatusMsg(null);
      try {
        const empNum = parseEmployeeNumericId(employeeKey);
        const [rows, bankMeta] = await Promise.all([
          accountingApi.beneficiaryBankAccounts(employeeKey),
          empNum
            ? accountingApi.employeeBankAccounts(empNum).catch(() => ({ banks: [] as BankOption[] }))
            : Promise.resolve({ banks: [] as BankOption[] }),
        ]);
        const list = rows || [];
        setAccounts(list);
        setBanks(bankMeta.banks ?? []);

        const preferred = pickPreferredAccount(list, mode, preferredAccountId);
        if (preferred) {
          setAccountId(preferred.id);
          const applied = applyAccount(preferred, employeeKey, employees);
          if (mode === 'account' && applied && needsAccountNumberPersist(preferred)) {
            const saved = await persistDerivedAccountNumber(preferred, employeeKey);
            setStatusMsg({
              type: 'ok',
              text: saved
                ? 'شماره حساب از شبا استخراج و روی حساب کارمند ذخیره شد.'
                : 'شماره حساب از شبا استخراج شد.',
            });
            if (saved) {
              await loadAccounts(employeeKey, preferred.id);
              return;
            }
          } else if (mode === 'account' && !applied) {
            setStatusMsg({
              type: 'info',
              text: 'شماره حساب یا شبای صادرات (۰۱۹) لازم است؛ در صورت داشتن شبا صادرات از روی آن ساخته می‌شود.',
            });
          } else if (mode === 'iban' && !applied && hasUsableCard(preferred)) {
            setStatusMsg({
              type: 'info',
              text: 'این حساب شبا ندارد؛ با «تبدیل کارت به شبا» می‌توانید شبا را بگیرید.',
            });
          }
        } else {
          setAccountId('');
        }
      } catch {
        setAccounts([]);
        setAccountId('');
        setBanks([]);
      } finally {
        setLoadingAccounts(false);
      }
    },
    [applyAccount, employees, mode, persistDerivedAccountNumber],
  );

  React.useEffect(() => {
    let cancelled = false;
    setLoadingEmployees(true);
    accountingApi
      .lookups()
      .then((res) => {
        if (cancelled) return;
        setEmployees((res.beneficiaries || []).filter((b) => b.type === 'employee'));
      })
      .catch(() => {
        if (!cancelled) setEmployees([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingEmployees(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    void loadAccounts(employeeId);
  }, [employeeId, loadAccounts]);

  const handleAccountChange = (nextId: string) => {
    setAccountId(nextId);
    setStatusMsg(null);
    const account = accounts.find((a) => a.id === nextId);
    if (!account) return;

    if (mode === 'account') {
      if (applyAccount(account, employeeId, employees)) {
        if (needsAccountNumberPersist(account)) {
          void persistDerivedAccountNumber(account, employeeId).then((saved) => {
            setStatusMsg({
              type: 'ok',
              text: saved
                ? 'شماره حساب از شبا استخراج و روی حساب کارمند ذخیره شد.'
                : 'شماره حساب از شبا استخراج شد.',
            });
            if (saved) void loadAccounts(employeeId, account.id);
          });
        }
        return;
      }
      setStatusMsg({
        type: 'err',
        text: 'برای انتقال صادراتی باید شماره حساب یا شبای بانک صادرات (کد ۰۱۹) ثبت شده باشد.',
      });
      return;
    }

    if (applyAccount(account, employeeId, employees)) return;
    if (hasUsableCard(account)) {
      setStatusMsg({
        type: 'info',
        text: 'این حساب شبا ندارد؛ روی «تبدیل کارت به شبا» بزنید.',
      });
    } else {
      setStatusMsg({
        type: 'err',
        text: 'برای این حساب شبا یا کارت کامل ثبت نشده؛ از «افزودن حساب» کارت یا شبا اضافه کنید.',
      });
    }
  };

  const handleConvertToSheba = async () => {
    if (!selectedAccount || !numericEmployeeId || !selectedCanConvert) return;
    setConverting(true);
    setStatusMsg({ type: 'info', text: 'در حال تبدیل کارت به شبا…' });
    try {
      const data = await accountingApi.fillEmployeeBankAccountIban(
        numericEmployeeId,
        selectedAccount.account_id,
      );
      const preferId = selectedAccount.id;
      await loadAccounts(employeeId, preferId);
      setStatusMsg({
        type: 'ok',
        text: data.account_number_filled
          ? 'شبا و شماره حساب ذخیره شد.'
          : data.already_had_iban
            ? 'این حساب از قبل شبا داشت.'
            : 'شبا ذخیره و در فرم پر شد.',
      });
    } catch (err) {
      setStatusMsg({
        type: 'err',
        text: err instanceof Error ? err.message : 'تبدیل کارت به شبا ناموفق بود.',
      });
    } finally {
      setConverting(false);
    }
  };

  const title = mode === 'account' ? 'انتخاب حساب از کارمندان سیستم' : 'انتخاب شبا از کارمندان سیستم';

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/80 p-3 md:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
          <UserRound className="h-4 w-4 text-slate-500" />
          {title}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          disabled={disabled || !numericEmployeeId || loadingAccounts}
          onClick={() => setShowBankModal(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          افزودن حساب / کارت
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <Label>کارمند</Label>
          <SearchableSelect
            value={employeeId}
            onChange={setEmployeeId}
            options={[
              { value: '', label: 'انتخاب نشده' },
              ...employees.map((e) => ({ value: e.id, label: e.name })),
            ]}
            placeholder={loadingEmployees ? 'در حال بارگذاری…' : 'جستجوی کارمند'}
            disabled={disabled || loadingEmployees}
          />
        </div>
        <div className="space-y-2">
          <Label>حساب بانکی</Label>
          <SearchableSelect
            value={accountId}
            onChange={handleAccountChange}
            options={
              accounts.length
                ? accounts.map((a) => ({
                    value: a.id,
                    label: accountOptionLabel(a, mode),
                  }))
                : [{ value: '', label: employeeId ? 'حسابی ثبت نشده' : 'ابتدا کارمند را انتخاب کنید' }]
            }
            placeholder={loadingAccounts ? 'در حال دریافت…' : 'انتخاب حساب'}
            disabled={disabled || !employeeId || loadingAccounts || accounts.length === 0}
          />
        </div>
      </div>

      {selectedCanConvert ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="h-8 gap-1.5"
            disabled={disabled || converting}
            onClick={() => void handleConvertToSheba()}
          >
            {converting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            تبدیل کارت به شبا
          </Button>
        </div>
      ) : null}

      {statusMsg ? (
        <p
          className={
            statusMsg.type === 'ok'
              ? 'text-xs text-emerald-700'
              : statusMsg.type === 'err'
                ? 'text-xs text-rose-700'
                : 'text-xs text-amber-700'
          }
        >
          {statusMsg.text}
        </p>
      ) : employeeId && !loadingAccounts && accounts.length === 0 ? (
        <p className="text-xs text-amber-700">
          برای این کارمند حسابی ثبت نشده. با «افزودن حساب / کارت» شماره{' '}
          {mode === 'account' ? 'حساب صادرات' : 'کارت یا شبا'} تعریف کنید.
        </p>
      ) : mode === 'account' ? (
        <p className="text-xs text-slate-500">
          کارمند را انتخاب کنید؛ شماره حساب یا شبای صادرات کافی است — در صورت نبودن شماره حساب، از روی شبا استخراج می‌شود.
        </p>
      ) : (
        <p className="text-xs text-slate-500">
          کارمند را انتخاب کنید؛ در صورت داشتن شبا خودکار پر می‌شود. می‌توانید حساب/کارت جدید هم از همین‌جا
          اضافه کنید.
        </p>
      )}

      {numericEmployeeId ? (
        <BankAccountModal
          show={showBankModal}
          onClose={() => setShowBankModal(false)}
          employeeId={numericEmployeeId}
          editAccount={null}
          banks={banks}
          onSuccess={() => {
            setShowBankModal(false);
            void loadAccounts(employeeId);
          }}
          onError={(msg) => setStatusMsg({ type: 'err', text: msg })}
        />
      ) : null}
    </div>
  );
};

export default BsiSystemShebaPicker;
